import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, locationsApi } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

const DEFAULT_INACTIVITY_TIMEOUT_MS = 3 * 60 * 60 * 1000; // 3 hours

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocationState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [locationAccessDenied, setLocationAccessDenied] = useState(false);
  const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState(DEFAULT_INACTIVITY_TIMEOUT_MS);
  const inactivityTimer = useRef(null);

  // Initialize from localStorage — auth itself lives in the httpOnly
  // `optsc_session` cookie (iter287). We keep the non-secret profile
  // fields here for instant boot hydration; if the cookie is missing or
  // expired the first API call returns 401 and the response interceptor
  // clears these + redirects to /login.
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedLocations = localStorage.getItem('locations');
    const savedCurrentLocation = localStorage.getItem('current_location');

    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      if (savedLocations) {
        setLocations(JSON.parse(savedLocations));
      }
      if (savedCurrentLocation) {
        setCurrentLocationState(JSON.parse(savedCurrentLocation));
      }
      const savedPermissions = localStorage.getItem('permissions');
      if (savedPermissions) {
        setPermissions(JSON.parse(savedPermissions));
      } else if (parsedUser.permissions) {
        setPermissions(parsedUser.permissions);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (credentials) => {
    // CRITICAL: Clear ALL previous user state BEFORE login to prevent cross-user leaks
    setLocationAccessDenied(false);
    setCurrentLocationState(null);
    setLocations([]);
    setPermissions({});
    localStorage.removeItem('current_location');
    localStorage.removeItem('locations');
    localStorage.removeItem('permissions');
    localStorage.removeItem('last_route');

    const response = await authApi.login(credentials);
    const { user: userData, locations: userLocations } = response.data;
    // NB: `response.data.token` is still returned by the backend for
    // API-client callers, but the browser uses the httpOnly cookie set
    // by /auth/login — we no longer write auth_token to localStorage.
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('locations', JSON.stringify(userLocations));
    localStorage.setItem('permissions', JSON.stringify(userData.permissions || {}));

    // Pre-fetch nav_order so redirect lands on correct first tab.
    // Uses the session cookie automatically (withCredentials in api.js).
    try {
      const settings = await api.get('/auth/app-settings');
      const navOrder = settings.data?.nav_order || [];
      if (navOrder.length > 0) {
        localStorage.setItem('nav_order', JSON.stringify(navOrder));
      } else {
        localStorage.removeItem('nav_order');
      }
    } catch {}

    setUser(userData);
    setLocations(userLocations);
    setPermissions(userData.permissions || {});

    // Set the NEW user's first location — never inherit previous user's location
    if (userLocations.length > 0) {
      const defaultLocation = userLocations[0];
      localStorage.setItem('current_location', JSON.stringify(defaultLocation));
      setCurrentLocationState(defaultLocation);
      // Initial login = the user IS clocking in here. Backend already opened
      // the session at this location during /login.
    }

    return response.data;
  }, []);

  const logout = useCallback((reason) => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    // Tell backend to close the session + clear cookie (best-effort)
    api.post('/auth/logout').catch(() => {});
    // Clear ALL non-secret client state (the auth cookie is cleared by /logout)
    localStorage.removeItem('user');
    localStorage.removeItem('locations');
    localStorage.removeItem('current_location');
    localStorage.removeItem('permissions');
    localStorage.removeItem('last_route');
    localStorage.removeItem('nav_order');
    localStorage.removeItem('call_notifications_muted');
    localStorage.removeItem('impersonated_by');
    sessionStorage.removeItem('location_switch_choice');
    setUser(null);
    setLocations([]);
    setCurrentLocationState(null);
    setPermissions({});
    setLocationAccessDenied(false);
    if (reason === 'inactivity') {
      localStorage.setItem('logout_reason', 'inactivity');
    }
  }, []);

  const setCurrentLocation = useCallback((location) => {
    if (!location) return;
    localStorage.setItem('current_location', JSON.stringify(location));
    setCurrentLocationState(location);
    setLocationAccessDenied(false);
    // Pre-warm caches for the new location in background
    if (location?.id) {
      api.post(`/locations/${location.id}/warm-cache`).catch(() => {});
    }
  }, []);


  const refreshLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll();
      setLocations(response.data);
      localStorage.setItem('locations', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Failed to refresh locations:', error);
      return [];
    }
  }, []);

  const hasPermission = useCallback((module, action) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    
    const modulePermissions = permissions[module] || [];
    if (modulePermissions.includes(action)) return true;
    // Backward compat: "general" permissions merged into "settings"
    if (module === 'settings') {
      const generalPerms = permissions['general'] || [];
      return generalPerms.includes(action);
    }
    return false;
  }, [user, permissions]);

  // ── Fetch inactivity timeout setting ──
  useEffect(() => {
    if (!user) return;
    api.get('/auth/app-settings').then(r => {
      const mins = r.data?.inactivity_timeout_minutes;
      if (mins && mins > 0) setInactivityTimeoutMs(mins * 60 * 1000);
    }).catch(() => {});
  }, [user]);

  // ── Inactivity auto-logout ──
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        logout('inactivity');
      }, inactivityTimeoutMs);
    };

    resetTimer();

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [user, logout, inactivityTimeoutMs]);

  // ── Session heartbeat for the "working hours" agent-validation layer ──
  // While the user has the app open we ping every 60s so the backend knows
  // they're on shift. The current location is included so the validation
  // layer can scope active staff to the right clinic.
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      api.post('/auth/ping', { location_id: currentLocation?.id || null }).catch(() => {
        // Silent — heartbeat is best-effort; auto-logout already handles dead sessions.
      });
    };
    ping(); // immediate ping after mount / login
    const handle = setInterval(ping, 60000); // every 60s
    return () => clearInterval(handle);
  }, [user, currentLocation]);

  const isAuthenticated = !!user;

  const value = {
    user,
    locations,
    currentLocation,
    loading,
    isAuthenticated,
    permissions,
    locationAccessDenied,
    login,
    logout,
    setCurrentLocation,
    setLocationAccessDenied,
    refreshLocations,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
